import { shouldSkipForceLogout } from "app/routes";

/**
 * Force logout and redirect to login page with session expired message
 * Called when any API returns 401 Unauthorized
 */
const forceLogout = () => {
  // Set session expired flag BEFORE removing token to avoid race condition
  // (PrivateRoute may redirect via React Router when token is removed)
  sessionStorage.setItem("session_expired", "true");
  localStorage.removeItem("token");
  window.location.href = "/login";
};

// Every backend error is the same envelope — {error: "module.code",
// user_error_msg: "Human readable", detail?} — including the ones DRF raises
// itself, which Core.exception_handler rewrites. There is no endpoint that
// answers with a bare {detail}.
//
// Existing call sites read `error.error` (and a few read `error.message`), so
// move the human message into those slots and keep the machine code under
// `error_code` for callers that branch on it (e.g. telling
// contract.no_signed_pdf apart from common.not_found on a download 404).
const normalizeError = (errorData, status) => {
  if (errorData.user_error_msg) {
    errorData.error_code = errorData.error;
    errorData.error = errorData.user_error_msg;
  }
  if (!errorData.message) {
    errorData.message = errorData.user_error_msg || errorData.detail;
  }
  errorData.status = status;
  return errorData;
};

export const request = async (url, options = {}) => {
  // Default headers
  const defaultHeaders = {
    "Content-Type": "application/json",
  };

  // Get token from localStorage (using your existing pattern)
  const token = localStorage.getItem("token");
  if (token) {
    defaultHeaders.Authorization = `Token ${token}`;
  }

  // Merge headers - if specific headers are passed, they override defaults
  // Special case: if body is FormData, don't set Content-Type (let browser handle it)
  let finalHeaders = { ...defaultHeaders };

  if (options.headers) {
    finalHeaders = { ...defaultHeaders, ...options.headers };
  }

  // If body is FormData, remove Content-Type to let browser set it with boundary
  if (options.body instanceof FormData) {
    delete finalHeaders["Content-Type"];
  }

  const config = {
    ...options,
    headers: finalHeaders,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      // Handle 401 Unauthorized - force logout (except on public/standalone routes)
      if (
        response.status === 401 &&
        !shouldSkipForceLogout(window.location.pathname)
      ) {
        forceLogout();
        // Return a promise that never resolves - page will redirect before it matters
        // This prevents any error UI from flashing before the redirect
        return new Promise(() => {});
      }

      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }

      throw normalizeError(errorData, response.status);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Binary sibling of `request` for endpoints that stream a file rather than
 * JSON — currently only the signed-contract PDF. `request` decides between
 * `.json()` and `.text()`, and `.text()` corrupts binary, so those callers need
 * this instead of the standard wrapper. Auth, the 401 force-logout and the
 * error-envelope handling stay identical, which is why it lives here rather
 * than as a raw `fetch` in a slice.
 *
 * Resolves to a Blob. The caller is responsible for revoking any object URL.
 */
export const requestBlob = async (url, options = {}) => {
  const headers = {};
  const token = localStorage.getItem("token");
  if (token) headers.Authorization = `Token ${token}`;

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    if (
      response.status === 401 &&
      !shouldSkipForceLogout(window.location.pathname)
    ) {
      forceLogout();
      return new Promise(() => {});
    }
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }
    throw normalizeError(errorData, response.status);
  }

  return await response.blob();
};
